// Cosmetic damage and dirt (RALLY_GOAL.md §5 P1-3): pure state, rendered by render/car-wear.ts.
// Dirt builds up with distance on loose and wet surfaces and washes off in water; the windscreen
// takes less (the wipers keep their arcs clean). Impacts leave dents on the body where it hit.
import type { SurfaceId } from '../sim/types';

/** Fraction of full dirt gained per metre driven on each surface (negative = cleans). */
export const DIRT_PER_M: Record<SurfaceId, number> = {
  gravel: 1 / 2500,
  loose: 1 / 1800,
  mud: 1 / 300,
  wet: 1 / 900,
  water: -1 / 40,
  grass: 1 / 4000,
  rock: 1 / 5000,
  tarmac: -1 / 6000,
  concrete: -1 / 6000,
};

export const MAX_DENTS = 8;
/** Contact force (N) below which an impact leaves no mark. */
export const DENT_FORCE = 14000;

export interface Dent {
  /** Car-local position on the body (m) and depth (m). */
  x: number;
  y: number;
  z: number;
  depth: number;
}

/** Body approximated as an ellipsoid (car-local centre and semi-axes) for placing dents. */
export interface BodyShape {
  cx: number;
  cy: number;
  cz: number;
  ax: number;
  ay: number;
  az: number;
}

export class WearState {
  dirt = 0;
  glassDirt = 0;
  readonly dents: Dent[] = [];
  /** Bumped when dents change (the renderer uploads them then). */
  version = 0;

  constructor(readonly body: BodyShape) {}

  reset(): void {
    this.dirt = 0;
    this.glassDirt = 0;
    this.dents.length = 0;
    this.version++;
  }

  /**
   * Per frame: `metres` driven, `surfaces` = share of wheels on each surface (sums to ≤ 1).
   */
  drive(metres: number, surfaces: Partial<Record<SurfaceId, number>>): void {
    if (metres <= 0) return;
    let rate = 0;
    for (const k in surfaces) rate += (surfaces[k as SurfaceId] ?? 0) * DIRT_PER_M[k as SurfaceId];
    const d = rate * metres;
    // Builds up quickly at first, then saturates; washing works on whatever is there.
    this.dirt = Math.min(1, Math.max(0, this.dirt + (d > 0 ? d * (1 - 0.6 * this.dirt) : d)));
    this.glassDirt = Math.min(0.75, Math.max(0, this.glassDirt + (d > 0 ? d * 0.55 * (1 - this.glassDirt) : d * 1.5)));
  }

  /**
   * An impact: `force` (N), `dir` = car-local unit direction from the car toward what it hit.
   * Returns true if it left a dent.
   */
  impact(force: number, dir: { x: number; y: number; z: number }): boolean {
    if (force < DENT_FORCE) return false;
    const b = this.body;
    const l = Math.hypot(dir.x, dir.y, dir.z);
    if (l < 1e-6) return false;
    const ux = dir.x / l;
    const uy = dir.y / l;
    const uz = dir.z / l;
    // Where the ray from the body centre along u leaves the ellipsoid.
    const t = 1 / Math.sqrt((ux / b.ax) ** 2 + (uy / b.ay) ** 2 + (uz / b.az) ** 2);
    const x = b.cx + ux * t;
    const y = b.cy + uy * t;
    const z = b.cz + uz * t;
    const depth = Math.min(0.07, 0.012 + ((force - DENT_FORCE) / 60000) * 0.05);
    // Close to an existing dent: deepen that one.
    for (const d of this.dents) {
      if (Math.hypot(d.x - x, d.y - y, d.z - z) < 0.3) {
        d.depth = Math.min(0.09, d.depth + depth * 0.6);
        this.version++;
        return true;
      }
    }
    if (this.dents.length >= MAX_DENTS) {
      let k = 0;
      for (let i = 1; i < this.dents.length; i++) if (this.dents[i].depth < this.dents[k].depth) k = i;
      this.dents.splice(k, 1);
    }
    this.dents.push({ x, y, z, depth });
    this.version++;
    return true;
  }
}
